import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Skeleton,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime } from '../lib/format';

interface Publication {
  id: string;
  catalogVersion: number;
  note: string | null;
  createdAt: string;
  publishedBy: string | null;
  rolledBackFromId: string | null;
  items: Array<{ action: string; operationKey: string; version: string }>;
}

export function CatalogVersionsPage({ role }: { role: string }) {
  const publications = useResource<{ items: Publication[] }>('/api/admin/catalog/publications');
  const toast = useToast();
  const [rollbackTarget, setRollbackTarget] = useState<Publication | null>(null);
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  return (
    <>
      <PageHeader
        title="Wersje katalogu"
        description="Każda publikacja jest niezmiennym wpisem historii. Wycofanie tworzy nową wersję przywracającą poprzednie wskaźniki — nic nie jest usuwane."
      />

      <Callout>
        Klienci, którzy przypięli konkretną wersję operacji, nie są dotknięci publikacją ani
        wycofaniem. Zmienia się wyłącznie wskaźnik wersji domyślnej.
      </Callout>

      {publications.loading ? (
        <Skeleton rows={5} />
      ) : (
        <Card tight>
          <DataTable
            rows={publications.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Katalog nie został jeszcze opublikowany"
                description="Opublikuj pierwszą wersję operacji, aby stworzyć wersję katalogu."
              />
            }
            columns={[
              {
                key: 'version',
                header: 'Wersja',
                render: (row) => (
                  <>
                    <strong>v{row.catalogVersion}</strong>
                    {row.rolledBackFromId && (
                      <div>
                        <Badge tone="warn">wycofanie</Badge>
                      </div>
                    )}
                  </>
                ),
              },
              { key: 'created', header: 'Data', render: (row) => formatDateTime(row.createdAt) },
              { key: 'by', header: 'Opublikował', render: (row) => row.publishedBy ?? 'system' },
              { key: 'note', header: 'Notatka', render: (row) => row.note ?? '—' },
              {
                key: 'items',
                header: 'Zmiany',
                render: (row) => (
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                    {row.items.map((item) => (
                      <li key={`${item.operationKey}@${item.version}`} className="ah-mono">
                        {item.action === 'ROLLBACK' ? '↩ ' : '✓ '}
                        {item.operationKey}@{item.version}
                      </li>
                    ))}
                  </ul>
                ),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  canWrite ? (
                    <Button size="sm" onClick={() => setRollbackTarget(row)}>
                      Przywróć
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      <ConfirmDialog
        open={rollbackTarget !== null}
        onOpenChange={(open) => !open && setRollbackTarget(null)}
        title="Przywrócić tę wersję katalogu?"
        description={
          rollbackTarget
            ? `Powstanie nowa wersja katalogu przywracająca wskaźniki domyślne z wersji v${rollbackTarget.catalogVersion}. Historia pozostaje nienaruszona.`
            : ''
        }
        confirmLabel="Przywróć"
        onConfirm={async () => {
          if (!rollbackTarget) return;
          try {
            const result = await api.post<{ catalogVersion: number }>(
              `/api/admin/catalog/publications/${rollbackTarget.id}/rollback`,
            );
            toast.success('Przywrócono', `Nowa wersja katalogu: ${result.catalogVersion}`);
            publications.reload();
          } catch (error) {
            const described = describeError(error);
            toast.error(described.title, described.body);
          }
        }}
      />
    </>
  );
}
